import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  API_BASE,
  ApiError,
  api,
  type EditionBrief,
  type EditionRow,
  type StagePlanRow,
  type WithdrawnPage,
} from '../lib/api'
import { Banner, Empty, ErrorNote, RecordGrid, Segmented } from '../components/Page'
import {
  Card,
  ConsoleHead,
  duration,
  longDate,
  megabytes,
} from '../components/admin/Console'

/** What the API answers when a file is handed to the pipeline. */
interface IngestQueued {
  queued: boolean
  job_id: string | null
  source_path: string
  publication: string
  pub_date: string | null
  message: string
}

interface StageSpec {
  key: string
  title: string
  pipeline: string
  requires: string[]
  implemented: boolean
  phase: string
}

type Metrics = Record<string, unknown>

/** A metric as text, without inventing a value that is not there. */
function metric(metrics: Metrics | undefined, key: string): string | null {
  const value = metrics?.[key]
  if (value === undefined || value === null) return null
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  return String(value)
}

function number(metrics: Metrics | undefined, key: string): number | null {
  const value = metrics?.[key]
  return typeof value === 'number' ? value : null
}

const STATUS_STYLE: Record<string, { colour: string; background: string }> = {
  ok: { colour: 'var(--jl-art)', background: 'transparent' },
  pending: { colour: 'var(--color-neutral-700)', background: 'var(--color-surface)' },
  queued: { colour: 'var(--color-neutral-700)', background: 'var(--color-surface)' },
  running: { colour: 'var(--color-neutral-700)', background: 'var(--color-surface)' },
  skipped: { colour: 'var(--color-neutral-600)', background: 'transparent' },
  not_implemented: { colour: 'var(--color-neutral-600)', background: 'transparent' },
  error: { colour: 'var(--color-accent-2-700)', background: 'var(--color-accent-2-100)' },
}

function statusStyle(status: string) {
  return STATUS_STYLE[status] ?? STATUS_STYLE.error
}

/**
 * Ingest control: an edition and its whole stage plan.
 *
 * The plan lists every stage the edition will pass through, including the ones
 * that do not exist yet. `not_implemented` is information rather than failure,
 * and `pending` is a pipeline waiting on work rather than a pipeline that
 * broke. Nothing here rounds a duration or hides a message.
 */
export function IngestPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { editionId } = useParams()
  const [registering, setRegistering] = useState(false)
  const [queued, setQueued] = useState<string | null>(null)
  const [batch, setBatch] = useState(false)

  const editions = useQuery({
    queryKey: ['admin', 'editions'],
    queryFn: () => api.get<EditionRow[]>('/admin/ingest/editions?limit=100'),
  })

  // The publication a slug names lives on the reader's edition list rather
  // than the admin row, so the header can print the slug the CLI takes.
  const briefs = useQuery({
    queryKey: ['editions', 'brief'],
    queryFn: () => api.get<EditionBrief[]>('/editions'),
  })

  const catalogue = useQuery({
    queryKey: ['admin', 'stages'],
    queryFn: () => api.get<StageSpec[]>('/admin/ingest/stages'),
  })

  const edition =
    editions.data?.find((row) => row.id === editionId) ?? editions.data?.[0] ?? undefined
  const brief = briefs.data?.find((row) => row.id === edition?.id)

  const plan = useQuery({
    queryKey: ['admin', 'plan', edition?.id],
    queryFn: () => api.get<StagePlanRow[]>(`/admin/ingest/editions/${edition!.id}/plan`),
    enabled: !!edition,
    refetchInterval: 15_000,
  })

  const runStage = useMutation({
    mutationFn: (input: { stage: string; force: boolean }) =>
      api.post<StagePlanRow>(`/admin/ingest/editions/${edition!.id}/stages/${input.stage}/run`, {
        force: input.force,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'plan'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] })
    },
  })

  const override = useMutation({
    mutationFn: (input: { route: 'A' | 'B'; reason: string }) =>
      api.post<EditionRow>(`/admin/ingest/editions/${edition!.id}/route`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'editions'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'plan'] })
    },
  })

  // Every title the archive holds, including one with no issue yet: a new
  // journal has to be offered here before its first upload.
  const titles = useQuery({
    queryKey: ['admin', 'publications'],
    queryFn: () => api.get<{ slug: string; name: string }[]>('/admin/publications'),
  })
  const publications = useMemo(
    () => (titles.data ?? []).map(({ slug, name }) => ({ slug, name })),
    [titles.data],
  )

  const remove = useMutation({
    mutationFn: (id: string) => api.del<void>(`/admin/ingest/editions/${id}`),
    onSuccess: () => {
      navigate('/admin/ingest')
      void queryClient.invalidateQueries({ queryKey: ['admin'] })
      void queryClient.invalidateQueries({ queryKey: ['editions'] })
    },
  })

  const registerForm = (
    <RegisterForm
      publications={publications}
      slug={brief?.publication_slug ?? publications[0]?.slug ?? ''}
      onDone={(message) => {
        setRegistering(false)
        setQueued(message)
        void queryClient.invalidateQueries({ queryKey: ['admin'] })
      }}
    />
  )

  const rows = useMemo(() => plan.data ?? [], [plan.data])
  const byStage = useMemo(() => {
    const map = new Map<string, StagePlanRow>()
    for (const row of rows) map.set(row.stage, row)
    return map
  }, [rows])
  const specs = useMemo(() => {
    const map = new Map<string, StageSpec>()
    for (const spec of catalogue.data ?? []) map.set(spec.key, spec)
    return map
  }, [catalogue.data])

  const register = byStage.get('00_register')
  const probe = byStage.get('01_probe')
  const raster = byStage.get('02_rasterise')
  const grid = byStage.get('A03_grid') ?? byStage.get('B03_grid')
  const layout = byStage.get('A04_layout') ?? byStage.get('B04_layout')

  // Until the plan is read, the fields derived from it say so. A dash where a
  // figure is still coming reads as "there is none", which is a lie.
  const planKnown = !!plan.data
  const totalMs = rows.reduce((sum, row) => sum + (row.duration_ms ?? 0), 0)
  const waiting = rows.filter((row) => row.status === 'pending' || row.status === 'not_implemented')

  async function runEverything() {
    for (const row of rows) {
      if (!row.implemented) continue
      try {
        await runStage.mutateAsync({ stage: row.stage, force: true })
      } catch {
        // A stage that refuses stops the run: the message is on its row, and
        // an operator re-runs from there rather than being told nothing.
        break
      }
    }
  }

  if (editions.isLoading) {
    return (
      <>
        <ConsoleHead title="Ingest" />
        <Card>
          <Empty>Reading the edition list.</Empty>
        </Card>
      </>
    )
  }

  if (!edition) {
    return (
      <>
        <ConsoleHead title="Ingest" />
        <Card>
          {registerForm}
          {queued ? (
            <div className="px-5 py-3.5">
              <Banner tone="info">{queued}</Banner>
            </div>
          ) : (
            <Empty>Nothing is registered yet. Upload the first issues above.</Empty>
          )}
        </Card>
      </>
    )
  }

  return (
    <>
      <ConsoleHead
        title="Ingest"
        note={`${editions.data?.length ?? 0} ${editions.data?.length === 1 ? 'edition' : 'editions'} registered`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {(editions.data?.length ?? 0) > 1 && (
              <select
                className="input-dense w-auto"
                aria-label="Edition"
                value={edition.id}
                onChange={(event) => navigate(`/admin/ingest/${event.target.value}`)}
              >
                {editions.data?.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.pub_date ?? 'undated'} · {row.source_filename}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              aria-expanded={batch}
              onClick={() => setBatch((open) => !open)}
            >
              Run on several issues
            </button>
          </div>
        }
      />

      {batch && (
        <BatchRunner
          editions={editions.data ?? []}
          onDone={(message) => {
            setBatch(false)
            setQueued(message)
          }}
        />
      )}

      <ErrorNote error={runStage.error ?? override.error ?? remove.error} />

      <Card>
        {/* The edition header. */}
        <div className="border-b border-n300 px-5 pb-3 pt-4">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div className="min-w-0">
              <div className="mb-[5px] truncate font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink2">
                {[
                  brief?.publication_slug,
                  edition.source_filename ?? 'no file',
                  `SHA ${edition.sha256.slice(0, 4)}…${edition.sha256.slice(-4)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <h2 className="text-[27px] font-semibold leading-[1.1] tracking-[-0.015em]">
                {longDate(edition.pub_date)}
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="rounded-[--radius-md] border px-[9px] py-[5px] font-mono text-[10.5px]"
                style={{
                  background:
                    edition.ingest_status === 'complete'
                      ? 'transparent'
                      : 'var(--color-accent-2-100)',
                  borderColor:
                    edition.ingest_status === 'complete'
                      ? 'var(--jl-art)'
                      : 'var(--color-accent-2-700)',
                  color:
                    edition.ingest_status === 'complete'
                      ? 'var(--jl-art)'
                      : 'var(--color-accent-2-700)',
                }}
              >
                {edition.ingest_status}
              </span>
              <button
                type="button"
                className="btn-secondary"
                disabled={override.isPending}
                onClick={() => {
                  const target = edition.route === 'A' ? 'B' : 'A'
                  const reason = window.prompt(
                    `Why route this edition to Pipeline ${target}? The reason is recorded with your name.`,
                  )
                  if (reason) override.mutate({ route: target, reason })
                }}
              >
                Override routing
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={runStage.isPending}
                onClick={() => void runEverything()}
              >
                Re-run all
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={remove.isPending}
                onClick={() => {
                  const ok = window.confirm(
                    `Delete the issue of ${longDate(edition.pub_date)} entirely: its pages, articles and original file? This cannot be undone. To take an issue out of view and keep it, withdraw it instead.`,
                  )
                  if (ok) remove.mutate(edition.id)
                }}
              >
                Delete issue
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setRegistering((value) => !value)}
              >
                Upload issues
              </button>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-6 border-t border-n200 pt-3">
            <Record label="Pages" value={edition.page_count ?? 'not probed'} />
            <Record
              label="Geometry"
              value={
                edition.page_w_pt && edition.page_h_pt
                  ? `${edition.page_w_pt} × ${edition.page_h_pt} pt`
                  : 'not probed'
              }
            />
            <Record
              label="Columns"
              value={
                edition.column_count
                  ? `${edition.column_count}${
                      metric(grid?.metrics, 'pitch_pt') ? ` @ ${metric(grid?.metrics, 'pitch_pt')} pt` : ''
                    }`
                  : 'not derived'
              }
            />
            <Record
              label="Source"
              value={
                planKnown
                  ? `${megabytes(number(register?.metrics, 'bytes'))}${
                      metric(raster?.metrics, 'max_ppi')
                        ? ` · ${metric(raster?.metrics, 'max_ppi')} ppi`
                        : ''
                    } ${edition.source_type}`
                  : edition.source_type
              }
            />
            <Record label="Registered" value={stamp(edition.created_at)} />
            <Record
              label="Blocks"
              value={planKnown ? (metric(layout?.metrics, 'blocks') ?? '0') : 'reading'}
            />
            <Record label="Duration" value={planKnown ? duration(totalMs) : 'reading'} />
          </div>
        </div>

        {registering && registerForm}

        {queued && (
          <div className="border-b border-n300 px-5 py-3.5">
            <Banner tone="info">{queued}</Banner>
          </div>
        )}

        <div className="flex min-h-0 flex-col xl:flex-row">
          {/* The stage plan. */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3 border-b border-n300 px-5 py-3">
              <div className="chrome text-[11px]">Stage plan</div>
              <div className="ml-auto flex flex-wrap gap-3">
                {(['ok', 'pending', 'not_implemented', 'error'] as const).map((key) => (
                  <div
                    key={key}
                    className="flex items-center gap-1.5 font-mono text-[9.5px] text-ink2"
                  >
                    <span
                      className="h-2 w-2"
                      style={{
                        background: statusStyle(key).colour,
                        border: key === 'not_implemented' ? '1px solid var(--color-neutral-500)' : 'none',
                      }}
                    />
                    {key}
                  </div>
                ))}
              </div>
            </div>

            {plan.isLoading ? (
              <Empty>Reading the plan.</Empty>
            ) : (
              <div className="overflow-x-auto">
                <div
                  className="grid min-w-[860px]"
                  style={{ gridTemplateColumns: '1.05fr 1.7fr 0.5fr 0.95fr 0.7fr 0.4fr 1.5fr auto' }}
                  role="table"
                  aria-label="Stage plan"
                >
                  {['Stage', 'Title', 'Pipe', 'Status', 'Duration', 'Try', 'Message', ''].map(
                    (head, index) => (
                      <div
                        key={head || `blank-${index}`}
                        className="border-b border-n300 bg-surface px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink2"
                      >
                        {head}
                      </div>
                    ),
                  )}
                  {rows.map((row) => {
                    const spec = specs.get(row.stage)
                    const style = statusStyle(row.status)
                    const dim = row.status === 'not_implemented'
                    const cell = 'border-b border-n200 px-3.5 py-2 font-mono text-[11.5px]'
                    return (
                      <Fragment key={row.stage}>
                        <div className={cell} style={{ opacity: dim ? 0.62 : 1 }}>
                          {row.stage}
                        </div>
                        <div
                          className="border-b border-n200 px-3.5 py-2 text-[13.5px]"
                          style={{ opacity: dim ? 0.62 : 1 }}
                        >
                          {spec?.title ?? ''}
                        </div>
                        <div className={cell} style={{ opacity: dim ? 0.62 : 1 }}>
                          {spec?.pipeline ?? ''}
                        </div>
                        <div
                          className={cell}
                          style={{ color: style.colour, background: style.background }}
                        >
                          {row.status}
                        </div>
                        <div className={cell} style={{ opacity: dim ? 0.62 : 1 }}>
                          {duration(row.duration_ms)}
                        </div>
                        <div className={cell} style={{ opacity: dim ? 0.62 : 1 }}>
                          {row.attempt}
                        </div>
                        <div
                          className={`${cell} text-ink2`}
                          style={{ opacity: dim ? 0.62 : 1 }}
                          title={row.message ?? undefined}
                        >
                          {row.message ?? (spec && !spec.implemented ? spec.phase : '')}
                        </div>
                        <div className="border-b border-n200 px-3.5 py-2">
                          {row.implemented ? (
                            <button
                              type="button"
                              className="font-mono text-[11.5px] text-accent underline-offset-2 hover:underline disabled:opacity-45"
                              disabled={runStage.isPending}
                              onClick={() =>
                                runStage.mutate({ stage: row.stage, force: row.status === 'ok' })
                              }
                            >
                              Run
                            </button>
                          ) : (
                            <span className="font-mono text-[11.5px] text-ink4">Run</span>
                          )}
                        </div>
                      </Fragment>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Why it went the way it went. */}
          <div className="w-full flex-none border-t border-n300 xl:w-[340px] xl:border-l xl:border-t-0">
            <div className="border-b border-n300 px-4 py-3">
              <div className="chrome text-[11px]">01_probe · the routing decision</div>
            </div>
            <div className="border-b border-n300 px-4 py-3.5">
              {!planKnown ? (
                <p className="text-[14.5px] text-ink2">Reading what the probe decided.</p>
              ) : (
              <>
              <div className="mb-3 flex items-baseline gap-2.5">
                <div className="font-mono text-[32px] leading-none text-accent">
                  {edition.route ?? '?'}
                </div>
                <div className="text-[15px] leading-[1.4] text-ink2">
                  {edition.route === 'A'
                    ? 'Routed to the digital-native pipeline.'
                    : edition.route === 'B'
                      ? 'Routed to the scanned-edition pipeline.'
                      : 'Not routed yet.'}
                </div>
              </div>
              <RecordGrid
                cols={1}
                rows={[
                  ['route', edition.route ?? 'undecided'],
                  [
                    'dictionary',
                    metric(probe?.metrics, 'dictionary_score')
                      ? `${((number(probe?.metrics, 'dictionary_score') ?? 0) * 100).toFixed(1)}% of ${Number(
                          metric(probe?.metrics, 'dictionary_tokens') ?? 0,
                        ).toLocaleString()} tokens`
                      : `${((edition.probe_score ?? 0) * 100).toFixed(1)}%`,
                  ],
                  ['source', metric(probe?.metrics, 'dictionary_source') ?? 'not recorded'],
                  [
                    'fonts',
                    metric(probe?.metrics, 'embedded_fonts') === 'yes'
                      ? 'embedded'
                      : metric(probe?.metrics, 'embedded_fonts') === 'no'
                        ? 'none embedded'
                        : 'not recorded',
                  ],
                  [
                    'raster',
                    metric(probe?.metrics, 'full_page_raster_ratio')
                      ? `${((number(probe?.metrics, 'full_page_raster_ratio') ?? 0) * 100).toFixed(0)}% full-page`
                      : 'not recorded',
                  ],
                  ['ppi seen', metric(probe?.metrics, 'raster_ppi_observed') ?? 'not recorded'],
                  ['pages', metric(probe?.metrics, 'page_count') ?? String(edition.page_count ?? '-')],
                  ['override', 'none'],
                ]}
              />
              <div className="mt-3">
                <Banner tone={edition.route === 'B' ? 'caution' : 'info'}>
                  {routingWhy(probe?.metrics, edition)}
                </Banner>
              </div>
              </>
              )}
            </div>

            <div className="border-b border-n300 px-4 py-3.5">
              <div className="mb-2 font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink2">
                02_rasterise · metrics
              </div>
              {!planKnown ? (
                <p className="text-[14px] text-ink2">Reading the render metrics.</p>
              ) : raster ? (
                <>
                  <RecordGrid
                    cols={1}
                    rows={[
                      ['pages', metric(raster.metrics, 'pages') ?? '-'],
                      ['rendered', metric(raster.metrics, 'rendered') ?? '-'],
                      ['reused', metric(raster.metrics, 'reused') ?? '-'],
                      ['target_ppi', metric(raster.metrics, 'target_ppi') ?? '-'],
                      ['native min', metric(raster.metrics, 'min_ppi') ?? '-'],
                      ['native max', metric(raster.metrics, 'max_ppi') ?? '-'],
                      ['upscaled', metric(raster.metrics, 'upscaled') === 'yes' ? 'yes' : '0'],
                      ['duration', duration(raster.duration_ms)],
                    ]}
                  />
                  <p className="mt-2.5 text-[14px] leading-[1.45] text-ink2">
                    {metric(raster.metrics, 'native_ppi_capped') === 'yes'
                      ? `Never upscaled: the widest image object measured ${metric(raster.metrics, 'max_ppi')} ppi, so the render capped there rather than at the ${metric(raster.metrics, 'target_ppi')} ppi target.`
                      : `Rendered at the ${metric(raster.metrics, 'target_ppi')} ppi target. A page is never rendered above the resolution its own imagery carries.`}
                  </p>
                </>
              ) : (
                <p className="text-[14px] text-ink2">This edition has not been rasterised yet.</p>
              )}
            </div>

            <div className="px-4 py-3.5">
              <div className="mb-2 font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink2">
                {!planKnown
                  ? 'The plan'
                  : waiting.length
                    ? `Why ${waiting.length} ${waiting.length === 1 ? 'stage is' : 'stages are'} not done`
                    : 'The plan is complete'}
              </div>
              <p className="mb-2.5 text-[14.5px] leading-[1.5]">
                {!planKnown
                  ? 'Reading which stages have run.'
                  : waiting.length
                  ? 'These stages are specified and registered but have not run to completion. Re-running the edition produces no errors and no partial data. The rows exist so an operator can see the whole plan and where it stopped.'
                    : "Every stage in this edition's plan has run. The rows above carry the duration and the message each one reported."}
              </p>
              <div className="font-mono text-[10px] leading-[1.7] text-ink2">
                PENDING IS NOT A FAILURE.
                <br />
                NOT_IMPLEMENTED IS INFORMATION, NOT A GAP.
              </div>
            </div>
          </div>
        </div>
      </Card>

      <RightsCard edition={edition} />
    </>
  )
}

/**
 * Rights requests: withdraw a page or the whole edition from view, and put it
 * back. The publisher decides; this is where the administrator carries the
 * decision out. Nothing is deleted or edited: a withdrawn page answers readers
 * with a notice and its items leave search until it is reinstated, and both
 * actions go to the audit log with the reason.
 */
function RightsCard({ edition }: { edition: EditionRow }) {
  const queryClient = useQueryClient()
  const [scope, setScope] = useState<'page' | 'edition'>('page')
  const [pageNumber, setPageNumber] = useState(1)
  const [reason, setReason] = useState('')
  const withdrawn = useQuery({
    queryKey: ['withdrawals'],
    queryFn: () => api.get<WithdrawnPage[]>('/admin/withdrawals'),
  })
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
    void queryClient.invalidateQueries({ queryKey: ['edition', edition.id] })
  }
  const withdraw = useMutation({
    mutationFn: () =>
      api.post<WithdrawnPage[]>('/admin/withdrawals', {
        edition_id: edition.id,
        page_number: scope === 'page' ? pageNumber : null,
        reason,
      }),
    onSuccess: () => {
      setReason('')
      refresh()
    },
  })
  const reinstate = useMutation({
    mutationFn: (pageId: string) => api.del<WithdrawnPage>(`/admin/withdrawals/${pageId}`),
    onSuccess: refresh,
  })
  const mine = (withdrawn.data ?? []).filter((row) => row.edition_id === edition.id)
  const pages = Math.max(1, edition.page_count ?? 1)
  const ready = reason.trim().length >= 3 && !withdraw.isPending

  return (
    <Card>
      <div className="border-b border-n300 px-5 pb-3 pt-4">
        <div className="chrome text-[11px]">Rights requests</div>
        <p className="mt-1 max-w-[80ch] text-[14.5px] leading-[1.45] text-ink2">
          Withdraw a page, or the whole edition, from view once the publisher has decided a rights
          request. Nothing is deleted: the page answers with a notice, its items leave search, and
          the withdrawal and its reason go to the audit log. Reinstating undoes all of it.
        </p>
      </div>
      <form
        className="flex flex-wrap items-end gap-3 border-b border-n300 bg-surface px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) withdraw.mutate()
        }}
      >
        <div className="w-[200px]">
          <label className="label" htmlFor="rights-scope">
            What to withdraw
          </label>
          <select
            id="rights-scope"
            className="input-dense"
            value={scope}
            onChange={(event) => setScope(event.target.value as 'page' | 'edition')}
          >
            <option value="page">One page</option>
            <option value="edition">The whole edition</option>
          </select>
        </div>
        {scope === 'page' && (
          <div className="w-[120px]">
            <label className="label" htmlFor="rights-page">
              Page
            </label>
            <input
              id="rights-page"
              className="input-dense font-mono"
              type="number"
              min={1}
              max={pages}
              value={pageNumber}
              onChange={(event) => setPageNumber(Number(event.target.value))}
            />
          </div>
        )}
        <div className="min-w-[280px] flex-1">
          <label className="label" htmlFor="rights-reason">
            Reason, as the publisher stated it
          </label>
          <input
            id="rights-reason"
            className="input-dense"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Rights holder's request of 12 September: the photograph on this page"
          />
        </div>
        <button type="submit" className="btn-primary" disabled={!ready}>
          {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
        </button>
      </form>
      <div className="px-5 py-4">
        {withdrawn.isLoading ? (
          <p className="text-[14.5px] text-ink2">Reading the withdrawals.</p>
        ) : mine.length === 0 ? (
          <Empty>Nothing in this edition is withdrawn.</Empty>
        ) : (
          <div className="flex flex-col gap-2">
            {mine.map((row) => (
              <div
                key={row.page_id}
                className="flex flex-wrap items-baseline gap-3 border-t border-n300 pt-2 first:border-t-0 first:pt-0"
              >
                <div className="w-[64px] font-mono text-[12px]">
                  p. {row.section_label ?? row.page_number}
                </div>
                <div className="min-w-0 flex-1 text-[14px] leading-[1.4]">
                  {row.withdrawn_reason}
                  <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink3">
                    {row.withdrawn_at ? stamp(row.withdrawn_at) : ''}
                    {row.withdrawn_by ? ` · ${row.withdrawn_by}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-secondary px-[11px] py-[5px] text-[13px]"
                  disabled={reinstate.isPending}
                  onClick={() => reinstate.mutate(row.page_id)}
                >
                  Reinstate
                </button>
              </div>
            ))}
          </div>
        )}
        <ErrorNote error={withdraw.error ?? reinstate.error ?? withdrawn.error} />
      </div>
    </Card>
  )
}

/** The routing decision in one sentence, built from what the probe measured. */
function routingWhy(metrics: Metrics | undefined, edition: EditionRow) {
  const ratio = typeof metrics?.full_page_raster_ratio === 'number' ? metrics.full_page_raster_ratio : null
  const score = typeof metrics?.dictionary_score === 'number' ? metrics.dictionary_score : edition.probe_score
  const fonts = metrics?.embedded_fonts === true

  if (ratio !== null && ratio >= 0.5) {
    return (
      <>
        {(ratio * 100).toFixed(0)}% of the sampled pages are a single full-page raster: a scan,
        whatever its text layer claims. Routing turns on that test, not on embedded fonts, because
        an OCR layer carries font subsets as readily as a typeset page does.
      </>
    )
  }
  if (score !== null && score !== undefined && fonts) {
    return (
      <>
        A real text layer: {(score * 100).toFixed(1)}% of sampled tokens read as words and the pages
        carry embedded fonts. Nothing here needs re-recognising.
      </>
    )
  }
  return (
    <>
      The text layer did not clear the bar: {((score ?? 0) * 100).toFixed(1)}% of sampled tokens read
      as words{fonts ? '' : ' and no fonts are embedded'}. The pages go through recognition rather
      than being trusted.
    </>
  )
}

/** A timestamp in the address style: the date, then the clock, never localised order. */
function stamp(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function Record({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="mb-[3px] font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink2">
        {label}
      </div>
      <div className="font-mono text-[13px]">{value}</div>
    </div>
  )
}

/**
 * Registering a file.
 *
 * Two shapes, one act. A librarian picks a PDF and it is uploaded; an operator
 * who has already dropped a batch onto the shared volume names one. Both end
 * with the file inside the worker's inbox and a job on the queue, because the
 * worker is a separate container and can read nothing else.
 *
 * The answer is 202 and a job id rather than a finished edition: an ingest
 * takes minutes, and a form that waited for one would be a timeout wearing a
 * success code. The stage plan is where it is then watched.
 */
/** The publication picker's "add one" entry; never a real slug, which has no
 *  underscores. */
const NEW_TITLE = '__new__'

function RegisterForm({
  publications,
  slug,
  onDone,
}: {
  publications: { slug: string; name: string }[]
  slug: string
  onDone: (message: string) => void
}) {
  const [mode, setMode] = useState<'upload' | 'inbox'>('upload')
  const [files, setFiles] = useState<File[]>([])
  const [filename, setFilename] = useState('')
  const [chosen, setPublication] = useState(slug)
  const [pubDate, setPubDate] = useState('')
  const creating = chosen === NEW_TITLE || !publications.length
  // The list may arrive after the form first renders, so an empty choice
  // falls to the first title rather than to nothing.
  const selectedSlug =
    chosen && chosen !== NEW_TITLE ? chosen : (publications[0]?.slug ?? '')
  const [progress, setProgress] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [processNow, setProcessNow] = useState(true)
  const queryClient = useQueryClient()

  const submit = useMutation({
    mutationFn: async () => {
      let publication = selectedSlug
      if (creating) {
        const created = await api.post<{ slug: string }>('/admin/publications', {
          name: newTitle.trim(),
        })
        publication = created.slug
        setPublication(created.slug)
        void queryClient.invalidateQueries({ queryKey: ['admin', 'publications'] })
      }
      if (mode === 'upload') {
        // One request per issue, one after another: a year of issues in one
        // request is a gigabyte the proxy and the API would both refuse, and
        // a failure part-way would lose which ones had gone. The date comes
        // from each filename unless a single file is given one.
        let queued: IngestQueued | null = null
        for (const [index, file] of files.entries()) {
          setProgress(`Uploading ${index + 1} of ${files.length}: ${file.name}`)
          // FormData sets its own boundary, so this request goes around the
          // JSON client rather than through it.
          const body = new FormData()
          body.append('file', file)
          body.append('publication', publication)
          if (pubDate && files.length === 1) body.append('pub_date', pubDate)
          body.append('process', processNow ? 'true' : 'false')
          const response = await fetch(`${API_BASE}/admin/ingest/editions`, {
            method: 'POST',
            credentials: 'same-origin',
            body,
          })
          const payload = await response.json().catch(() => null)
          if (!response.ok) {
            const reason = payload?.detail ?? `The upload was refused (${response.status}).`
            throw new ApiError(
              response.status,
              `${file.name}: ${reason}${index ? ` The ${index} before it were queued.` : ''}`,
              payload,
            )
          }
          queued = payload as IngestQueued
        }
        setProgress('')
        return {
          ...(queued as IngestQueued),
          message: !processNow
            ? `${files.length} ${files.length === 1 ? 'issue' : 'issues'} uploaded and waiting. Pick them under Run on several issues when you want them processed.`
            : files.length > 1
              ? `${files.length} issues queued for the worker. Each appears in the list once stage 00 has registered it.`
              : (queued as IngestQueued).message,
        }
      }
      return api.post<IngestQueued>('/admin/ingest/editions', {
        filename,
        publication,
        pub_date: pubDate || null,
        process: processNow,
      })
    },
    onSuccess: (result) => onDone(result.message),
    onError: () => setProgress(''),
  })

  const ready =
    (creating ? !!newTitle.trim() : !!selectedSlug) &&
    (mode === 'upload' ? files.length > 0 : !!filename.trim())

  return (
    <form
      className="border-b border-n300 bg-surface px-5 py-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (ready) submit.mutate()
      }}
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="chrome text-[11px]">Register a file</div>
        <Segmented<'upload' | 'inbox'>
          size="sm"
          ariaLabel="Where the file is"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'upload', label: 'Upload a PDF' },
            { value: 'inbox', label: 'Already in the inbox' },
          ]}
        />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <label className="label" htmlFor="ingest-file">
            {mode === 'upload' ? 'The PDFs, one per issue' : 'Filename in the worker inbox'}
          </label>
          {mode === 'upload' ? (
            <input
              id="ingest-file"
              className="input-dense font-mono file:mr-3 file:rounded-[--radius-sm] file:border file:border-rule2 file:bg-paper file:px-2 file:py-[3px] file:font-mono file:text-[12px] file:text-body"
              type="file"
              accept="application/pdf,.pdf"
              multiple
              required
              onChange={(event) => setFiles(Array.from(event.target.files ?? []))}
            />
          ) : (
            <input
              id="ingest-file"
              className="input-dense font-mono"
              placeholder="CJ_1990.12.19.pdf"
              required
              value={filename}
              onChange={(event) => setFilename(event.target.value)}
            />
          )}
        </div>

        <div className="w-[200px]">
          <label className="label" htmlFor="ingest-pub">
            Publication
          </label>
          <select
            id="ingest-pub"
            className="input-dense"
            value={creating ? NEW_TITLE : selectedSlug}
            onChange={(event) => setPublication(event.target.value)}
          >
            {publications.map((entry) => (
              <option key={entry.slug} value={entry.slug}>
                {entry.name}
              </option>
            ))}
            <option value={NEW_TITLE}>New publication…</option>
          </select>
        </div>

        {creating && (
          <div className="w-[220px]">
            <label className="label" htmlFor="ingest-new-title">
              Name of the new publication
            </label>
            <input
              id="ingest-new-title"
              className="input-dense"
              required
              value={newTitle}
              onChange={(event) => setNewTitle(event.target.value)}
            />
          </div>
        )}

        <div className="w-[160px]">
          <label className="label" htmlFor="ingest-date">
            Date, if known
          </label>
          <input
            id="ingest-date"
            className="input-dense font-mono"
            type="date"
            value={pubDate}
            disabled={mode === 'upload' && files.length > 1}
            onChange={(event) => setPubDate(event.target.value)}
          />
        </div>

        <div className="w-[210px]">
          <label className="label" htmlFor="ingest-process">
            After upload
          </label>
          <select
            id="ingest-process"
            className="input-dense"
            value={processNow ? 'now' : 'later'}
            onChange={(event) => setProcessNow(event.target.value === 'now')}
          >
            <option value="now">Process now</option>
            <option value="later">Upload only, run later</option>
          </select>
        </div>

        <button type="submit" className="btn-primary" disabled={!ready || submit.isPending}>
          {submit.isPending
            ? 'Uploading…'
            : files.length > 1 && mode === 'upload'
              ? `Register ${files.length} issues`
              : 'Register'}
        </button>
      </div>

      {progress && <p className="mt-2.5 font-mono text-[12px] text-body">{progress}</p>}
      <p className="mt-2.5 font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
        Pick one PDF or a whole year of them. Each lands in the worker inbox with a job on the queue,
        dated from its filename (CJ_2026-05-01.pdf) unless one file is given a date here.
      </p>

      <ErrorNote error={submit.error} />
    </form>
  )
}

/** What a batch run can start from, by what changed. */
const START_CHOICES: { value: string; label: string; hint: string }[] = [
  {
    value: 'all',
    label: 'Everything, from the file',
    hint: 'For issues uploaded to run later, or to start an issue over.',
  },
  {
    value: 'extraction',
    label: 'From text extraction on',
    hint: 'After an improvement to how pages are read: layout, text, photographs, then everything after.',
  },
  {
    value: 'assembly',
    label: 'From article assembly on',
    hint: 'After a change to how articles are put together: assembly, classification, the index.',
  },
  {
    value: 'index',
    label: 'Search index and exports',
    hint: 'After a change to search: the index, the ALTO pages and the review queue.',
  },
]

/**
 * Several issues, one choice of what to run on each.
 *
 * A year of issues is uploaded to run later, or an improvement to the
 * pipeline is applied to every issue already held; either way it is one tick
 * per issue and one choice, rather than a stage plan opened issue by issue.
 * Each issue becomes its own job, so the worker takes them in turn and each
 * one's progress is in its stage plan.
 */
function BatchRunner({
  editions,
  onDone,
}: {
  editions: EditionRow[]
  onDone: (message: string) => void
}) {
  const queryClient = useQueryClient()
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [start, setStart] = useState('all')
  const ordered = useMemo(
    () => [...editions].sort((a, b) => (b.pub_date ?? '').localeCompare(a.pub_date ?? '')),
    [editions],
  )
  const waiting = ordered.filter((row) => row.ingest_status === 'registered')
  const choice = START_CHOICES.find((entry) => entry.value === start) ?? START_CHOICES[0]

  const run = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>('/admin/ingest/runs', { edition_ids: [...picked], start }),
    onSuccess: (result) => {
      setPicked(new Set())
      void queryClient.invalidateQueries({ queryKey: ['admin'] })
      onDone(result.message)
    },
  })

  const toggle = (id: string) =>
    setPicked((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Card>
      <div className="border-b border-n300 px-5 py-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="chrome mr-2 text-[11px]">Run on several issues</div>
          <button
            type="button"
            className="btn-ghost text-[13px]"
            onClick={() => setPicked(new Set(ordered.map((row) => row.id)))}
          >
            All {ordered.length}
          </button>
          {waiting.length > 0 && (
            <button
              type="button"
              className="btn-ghost text-[13px]"
              onClick={() => {
                setPicked(new Set(waiting.map((row) => row.id)))
                setStart('all')
              }}
            >
              Waiting to run ({waiting.length})
            </button>
          )}
          <button type="button" className="btn-ghost text-[13px]" onClick={() => setPicked(new Set())}>
            None
          </button>
        </div>

        <div className="max-h-[320px] overflow-y-auto rounded-[--radius-md] border border-n300">
          {ordered.map((row) => (
            <label
              key={row.id}
              className="flex cursor-pointer items-center gap-3 border-b border-n200 px-3 py-2 last:border-b-0"
            >
              <input
                type="checkbox"
                checked={picked.has(row.id)}
                onChange={() => toggle(row.id)}
              />
              <span className="min-w-[150px] text-[14px]">
                {row.pub_date ? longDate(row.pub_date) : 'Undated'}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-ink2">
                {row.source_filename ?? 'no file'}
              </span>
              <span className="font-mono text-[11px] text-ink2">{row.ingest_status}</span>
            </label>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div className="w-[260px]">
            <label className="label" htmlFor="batch-start">
              What to run
            </label>
            <select
              id="batch-start"
              className="input-dense"
              value={start}
              onChange={(event) => setStart(event.target.value)}
            >
              {START_CHOICES.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="btn-primary"
            disabled={!picked.size || run.isPending}
            onClick={() => run.mutate()}
          >
            {run.isPending
              ? 'Queuing…'
              : `Run on ${picked.size} ${picked.size === 1 ? 'issue' : 'issues'}`}
          </button>
        </div>
        <p className="mt-2 text-[13.5px] text-ink2">{choice.hint}</p>
        <ErrorNote error={run.error} />
      </div>
    </Card>
  )
}
